import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Trash2, Download, MessageSquare, Check, Loader2, MessageCircle, RotateCcw, Ban, ChevronDown, Plus, X } from "lucide-react";

export default function AnalysisDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackClassification, setFeedbackClassification] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [referenceImages, setReferenceImages] = useState(null);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [addingToBlacklist, setAddingToBlacklist] = useState(false);
  
  // Novos estados para feedback expandido
  const [availableMoveis, setAvailableMoveis] = useState([]);
  const [feedbackCorrectMovel, setFeedbackCorrectMovel] = useState("");
  const [feedbackBlacklistItem, setFeedbackBlacklistItem] = useState("");
  const [feedbackBlacklistReason, setFeedbackBlacklistReason] = useState("");

  useEffect(() => {
    fetchAnalysis();
    fetchAvailableMoveis();
  }, [id]);

  useEffect(() => {
    // Carregar imagens de referência quando analysis estiver disponível
    if (analysis?.category_name) {
      fetchReferenceImages(analysis.category_name);
    }
  }, [analysis?.category_name]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      // Check if feedback was already given
      if (response.data.user_feedback) {
        setFeedbackSubmitted(true);
        setFeedbackClassification(response.data.user_feedback);
        if (response.data.feedback_correct_movel) {
          setFeedbackCorrectMovel(response.data.feedback_correct_movel);
        }
      }
    } catch (error) {
      toast.error("Análise não encontrada");
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMoveis = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/gabaritos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableMoveis(response.data.moveis || []);
    } catch (error) {
      console.log("Não foi possível carregar lista de móveis");
    }
  };

  const fetchReferenceImages = async (movelName) => {
    setLoadingReferences(true);
    try {
      const response = await axios.get(`${API}/gabarito/${encodeURIComponent(movelName)}/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferenceImages(response.data.images);
    } catch (error) {
      console.log("Imagens de referência não disponíveis");
      setReferenceImages(null);
    } finally {
      setLoadingReferences(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Tem certeza que deseja excluir esta análise?")) return;
    
    try {
      await axios.delete(`${API}/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Análise excluída");
      navigate('/history');
    } catch (error) {
      toast.error("Erro ao excluir análise");
    }
  };

  const handleDownloadImage = (imageData, filename) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    link.click();
  };

  const handleAddToBlacklist = async () => {
    if (!analysis?.category_name) return;
    
    const confirmed = window.confirm(
      `Tem certeza que deseja bloquear o móvel "${analysis.category_name}"?\n\n` +
      `Isso fará com que este tipo de móvel não seja mais analisado automaticamente.`
    );
    
    if (!confirmed) return;
    
    setAddingToBlacklist(true);
    try {
      await axios.post(`${API}/settings/blacklist/add`, {
        movel_name: analysis.category_name,
        reason: `Bloqueado a partir da análise ${id}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Móvel "${analysis.category_name}" adicionado à lista de bloqueio`);
    } catch (error) {
      toast.error("Erro ao bloquear móvel");
    } finally {
      setAddingToBlacklist(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOccupancyColor = (value) => {
    if (value >= 80) return "#34C759";
    if (value >= 50) return "#FF9500";
    return "#FF3B30";
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'BOA': return "#34C759";
      case 'RUIM': return "#FF9500";
      case 'VAZIA': return "#FF3B30";
      case 'CRITICA': return "#8B0000";
      default: return "#6B7280";
    }
  };

  const getClassificationLabel = (classification) => {
    switch (classification) {
      case 'BOA': return "Boa";
      case 'RUIM': return "Ruim";
      case 'VAZIA': return "Vazia";
      case 'CRITICA': return "Crítica";
      default: return classification || "—";
    }
  };

  const getOccupancyLabel = (value) => {
    if (value >= 80) return "Ótimo";
    if (value >= 50) return "Moderado";
    return "Baixo";
  };

  const handleFeedbackSubmit = async () => {
    // Precisa ter pelo menos uma das opções preenchidas
    if (!feedbackClassification && !feedbackCorrectMovel && !feedbackBlacklistItem) {
      toast.error("Preencha pelo menos um campo de feedback");
      return;
    }

    setSubmittingFeedback(true);
    try {
      await axios.post(
        `${API}/analysis/${id}/feedback`,
        {
          user_classification: feedbackClassification || null,
          correct_movel: feedbackCorrectMovel || null,
          blacklist_item: feedbackBlacklistItem || null,
          blacklist_reason: feedbackBlacklistReason || null,
          user_comment: feedbackComment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackSubmitted(true);
      
      // Se adicionou item à blacklist, mostrar mensagem específica
      if (feedbackBlacklistItem) {
        toast.success(`"${feedbackBlacklistItem}" adicionado à lista de itens a ignorar!`);
      } else {
        toast.success("Feedback enviado! Obrigado por ajudar a melhorar a IA.");
      }
    } catch (error) {
      toast.error("Erro ao enviar feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-screen">
          <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin rounded-full" />
        </div>
      </Sidebar>
    );
  }

  if (!analysis) return null;

  return (
    <Sidebar>
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="analysis-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/history')}
            className="h-10 w-10 hover:bg-zinc-100"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
          </Button>
          <div className="flex-1">
            <h1 className="font-chivo font-bold text-2xl tracking-tight">Detalhes da Análise</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {analysis.source === 'whatsapp' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700">
                  <MessageCircle size={12} />
                  WhatsApp
                </span>
              )}
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-zinc-100">
                {analysis.category_name}
              </span>
              {analysis.store_name && analysis.store_name !== 'WhatsApp' && (
                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  {analysis.store_name}
                </span>
              )}
              <span className="text-xs text-zinc-400 flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(analysis.created_at)}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleAddToBlacklist}
              disabled={addingToBlacklist}
              data-testid="blacklist-movel-btn"
              className="h-10 px-4 rounded-none border-zinc-200 text-amber-600 hover:bg-amber-50 hover:border-amber-200 gap-2"
              title="Bloquear este tipo de móvel de futuras análises"
            >
              {addingToBlacklist ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Ban size={16} strokeWidth={1.5} />
              )}
              Bloquear Móvel
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              data-testid="delete-analysis-btn"
              className="h-10 px-4 rounded-none border-zinc-200 text-red-600 hover:bg-red-50 hover:border-red-200 gap-2"
            >
              <Trash2 size={16} strokeWidth={1.5} />
              Excluir
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Result Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-zinc-200 p-6 sticky top-6">
              <h2 className="text-sm font-medium text-zinc-500 mb-4">Resultado da Análise</h2>
              
              {/* Classification Badge */}
              <div className="text-center mb-6 p-4 border" style={{ 
                backgroundColor: `${getClassificationColor(analysis.classification)}15`,
                borderColor: getClassificationColor(analysis.classification)
              }}>
                <p className="text-xs text-zinc-500 mb-1">Classificação</p>
                <p 
                  className="font-chivo font-black text-3xl"
                  style={{ color: getClassificationColor(analysis.classification) }}
                  data-testid="classification-value"
                >
                  {getClassificationLabel(analysis.classification)}
                </p>
              </div>
              
              {/* Occupancy Circle */}
              <div className="relative w-40 h-40 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke={getOccupancyColor(analysis.occupancy_percentage)}
                    strokeWidth="12"
                    strokeLinecap="square"
                    strokeDasharray={`${(analysis.occupancy_percentage / 100) * 440} 440`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span 
                    className="font-chivo font-black text-4xl"
                    style={{ color: getOccupancyColor(analysis.occupancy_percentage) }}
                    data-testid="occupancy-value"
                  >
                    {analysis.occupancy_percentage}%
                  </span>
                  <span className="text-xs text-zinc-500 mt-1">
                    {getOccupancyLabel(analysis.occupancy_percentage)}
                  </span>
                </div>
              </div>

              {/* Status Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Ocupação</span>
                  <span className="font-medium">{analysis.occupancy_percentage}%</span>
                </div>
                <div className="h-2 bg-zinc-100 overflow-hidden">
                  <div 
                    className="h-full transition-all duration-500"
                    style={{ 
                      width: `${analysis.occupancy_percentage}%`,
                      backgroundColor: getOccupancyColor(analysis.occupancy_percentage)
                    }}
                  />
                </div>
              </div>

              {/* Empty Shelves Info */}
              {analysis.empty_shelves_count > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200">
                  <p className="text-xs text-red-800">
                    <span className="font-bold">{analysis.empty_shelves_count}</span> prateleira(s) vazia(s)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Image */}
            <div className="bg-white border border-zinc-200 p-6">
              <h2 className="font-chivo font-bold text-lg mb-4">Imagem Analisada</h2>
              {analysis.current_image ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                      Foto enviada para análise
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadImage(analysis.current_image, `atual-${id}.jpg`)}
                      className="h-7 px-2 text-xs"
                    >
                      <Download size={12} className="mr-1" />
                      Baixar
                    </Button>
                  </div>
                  <div className="border border-zinc-200 bg-zinc-50 aspect-video overflow-hidden">
                    <img 
                      src={analysis.current_image} 
                      alt="Imagem analisada"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 p-8 text-center">
                  <p className="text-zinc-500 text-sm">
                    Imagem não disponível para esta análise.
                  </p>
                </div>
              )}
            </div>

            {/* Reference Images - 4 Classifications */}
            <div className="bg-white border border-zinc-200 p-6">
              <h2 className="font-chivo font-bold text-lg mb-4">Imagens de Referência do Gabarito</h2>
              <p className="text-sm text-zinc-500 mb-4">
                Compare a imagem analisada com os exemplos de cada classificação para {analysis.category_name}
              </p>
              
              {loadingReferences ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                  <span className="ml-2 text-sm text-zinc-500">Carregando referências...</span>
                </div>
              ) : referenceImages ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['Boa', 'Ruim', 'Vazia', 'Critica'].map((classification) => {
                    const img = referenceImages[classification];
                    const isCurrentClassification = analysis.classification?.toUpperCase() === classification.toUpperCase();
                    const classColors = {
                      'Boa': { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700' },
                      'Ruim': { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700' },
                      'Vazia': { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700' },
                      'Critica': { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700' }
                    };
                    const colors = classColors[classification];
                    
                    return (
                      <div 
                        key={classification} 
                        className={`border-2 ${isCurrentClassification ? colors.border : 'border-zinc-200'} ${isCurrentClassification ? colors.bg : ''}`}
                      >
                        <div className={`p-2 text-center ${isCurrentClassification ? colors.bg : 'bg-zinc-100'}`}>
                          <p className={`text-xs font-bold uppercase ${isCurrentClassification ? colors.text : 'text-zinc-600'}`}>
                            {classification}
                            {isCurrentClassification && ' (IA)'}
                          </p>
                        </div>
                        {img ? (
                          <div className="aspect-square overflow-hidden bg-zinc-50">
                            <img 
                              src={img} 
                              alt={`Referência ${classification}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="aspect-square bg-zinc-100 flex items-center justify-center">
                            <p className="text-xs text-zinc-400">Sem imagem</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 p-8 text-center">
                  <p className="text-zinc-500 text-sm">
                    Imagens de referência não disponíveis para este tipo de móvel.
                  </p>
                </div>
              )}
            </div>

            {/* Analysis Details */}
            <div className="bg-white border border-zinc-200 p-6">
              <h2 className="font-chivo font-bold text-lg mb-4">Detalhes da Análise</h2>
              <div className="bg-zinc-50 border border-zinc-200 p-4">
                <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
                  {analysis.analysis_details}
                </p>
              </div>
            </div>

            {/* Feedback Section */}
            <div className="bg-white border border-zinc-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare size={18} className="text-zinc-500" />
                <h2 className="font-chivo font-bold text-lg">Feedback da Análise</h2>
              </div>
              
              {feedbackSubmitted ? (
                <div className="bg-green-50 border border-green-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Check className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="text-sm text-green-800 font-medium">Feedback registrado!</p>
                        {feedbackClassification && (
                          <p className="text-xs text-green-600 mt-1">
                            Classificação: <strong>{getClassificationLabel(feedbackClassification)}</strong>
                          </p>
                        )}
                        {feedbackCorrectMovel && (
                          <p className="text-xs text-green-600 mt-0.5">
                            Móvel correto: <strong>{feedbackCorrectMovel}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFeedbackSubmitted(false);
                        setFeedbackClassification("");
                        setFeedbackCorrectMovel("");
                        setFeedbackBlacklistItem("");
                        setFeedbackBlacklistReason("");
                        setFeedbackComment("");
                      }}
                      className="rounded-none gap-2 text-zinc-600 hover:text-zinc-900"
                      data-testid="redo-feedback-btn"
                    >
                      <RotateCcw size={14} />
                      Refazer Feedback
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Grupo 1: Classificação Correta */}
                  <div className="bg-zinc-50 border border-zinc-200 p-4">
                    <h3 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 bg-blue-600 text-white text-xs flex items-center justify-center rounded-full">1</span>
                      Classificação Correta
                    </h3>
                    <p className="text-xs text-zinc-500 mb-3">
                      A IA classificou como <strong style={{ color: getClassificationColor(analysis.classification) }}>
                      {getClassificationLabel(analysis.classification)}</strong>. Está correta ou deveria ser outra?
                    </p>
                    
                    <div className="grid grid-cols-4 gap-2">
                      {['BOA', 'RUIM', 'VAZIA', 'CRITICA'].map((cls) => (
                        <button
                          key={cls}
                          onClick={() => setFeedbackClassification(feedbackClassification === cls ? "" : cls)}
                          data-testid={`feedback-${cls.toLowerCase()}`}
                          className={`p-2.5 border text-xs font-medium transition-all ${
                            feedbackClassification === cls
                              ? 'border-zinc-900 bg-zinc-900 text-white'
                              : 'border-zinc-200 hover:border-zinc-400 bg-white'
                          }`}
                        >
                          {getClassificationLabel(cls)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Grupo 2: Móvel Correto */}
                  <div className="bg-zinc-50 border border-zinc-200 p-4">
                    <h3 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 bg-orange-500 text-white text-xs flex items-center justify-center rounded-full">2</span>
                      Móvel Correto
                    </h3>
                    <p className="text-xs text-zinc-500 mb-3">
                      A IA identificou como <strong>{analysis.category_name}</strong>. Era outro móvel?
                    </p>
                    
                    <div className="relative">
                      <select
                        value={feedbackCorrectMovel}
                        onChange={(e) => setFeedbackCorrectMovel(e.target.value)}
                        data-testid="feedback-movel-select"
                        className="w-full p-2.5 border border-zinc-200 bg-white text-sm appearance-none cursor-pointer hover:border-zinc-400 focus:outline-none focus:border-zinc-900"
                      >
                        <option value="">— O móvel identificado está correto —</option>
                        {availableMoveis
                          .filter(m => m !== analysis.category_name)
                          .sort()
                          .map((movel) => (
                            <option key={movel} value={movel}>
                              {movel.replace(/_/g, ' ')}
                            </option>
                          ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Grupo 3: Adicionar à Blacklist */}
                  <div className="bg-zinc-50 border border-zinc-200 p-4">
                    <h3 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">3</span>
                      Não Deveria Analisar
                    </h3>
                    <p className="text-xs text-zinc-500 mb-3">
                      Se esta imagem mostra algo que <strong>não</strong> deveria ser analisado (ex: caixa em carrinho, carro, foto aleatória), descreva o que é:
                    </p>
                    
                    <div className="space-y-2">
                      <Input
                        value={feedbackBlacklistItem}
                        onChange={(e) => setFeedbackBlacklistItem(e.target.value)}
                        placeholder="Ex: Caixa em carrinho, Carro, Pessoa, etc."
                        data-testid="feedback-blacklist-item"
                        className="rounded-none text-sm"
                      />
                      {feedbackBlacklistItem && (
                        <Input
                          value={feedbackBlacklistReason}
                          onChange={(e) => setFeedbackBlacklistReason(e.target.value)}
                          placeholder="Motivo (opcional): Por que não deveria analisar?"
                          data-testid="feedback-blacklist-reason"
                          className="rounded-none text-sm"
                        />
                      )}
                    </div>
                  </div>

                  {/* Comentário Geral */}
                  <div className="bg-zinc-50 border border-zinc-200 p-4">
                    <Label className="text-xs text-zinc-500 mb-2 block">Comentário adicional (opcional)</Label>
                    <Textarea
                      value={feedbackComment}
                      onChange={(e) => setFeedbackComment(e.target.value)}
                      placeholder="Ex: A prateleira do meio estava completamente vazia..."
                      className="rounded-none resize-none text-sm bg-white"
                      rows={2}
                      data-testid="feedback-comment"
                    />
                  </div>
                  
                  <Button
                    onClick={handleFeedbackSubmit}
                    disabled={(!feedbackClassification && !feedbackCorrectMovel && !feedbackBlacklistItem) || submittingFeedback}
                    data-testid="submit-feedback-btn"
                    className="w-full h-10 rounded-none bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {submittingFeedback ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      "Enviar Feedback"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}
